import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { detectRuntimeEnvFromBrowser, detectBeTargetEnvFromBrowser } from '@/lib/config/runtimeEnv';

export const ROUTE_TITLES: Record<string, string> = {
  '/influencers': 'Influencers',
  '/dashboard': 'Dashboard',
  '/campaigns': 'Campaigns',
  '/campaigns/details': 'Campaign Details',
  '/campaigns/edit': 'Campaign Editor',
  '/campaigns/results': 'Campaign Results',
  '/summary': 'Campaign Summary',
  '/campaign_post_candidates': 'Post Candidates',
  '/analytics': 'Analytics',
  '/stats': 'Analytics',
  '/analytics/campaign': 'Campaign Analytics',
  '/stats/campaign': 'Campaign Analytics',
  '/stats/dedicated': 'Dedicated Stats',
  '/search': 'Search',
  '/search/game': 'Search Game',
  '/advanced_search': 'Advanced Search',
  '/blacklist': 'Blacklist',
  '/candidates': 'Candidates',
  '/communications': 'Communications',
  '/payments': 'Payments',
  '/payments/reviews': 'Payments Reviews',
  '/templates': 'Templates',
  '/admins': 'Admins',
  '/advertisers': 'Advertisers',
  '/influencer': 'Influencer',
  '/general': 'General Posts',
  '/influencers/add': 'Add Influencer',
  '/invitations': 'Invitations',
  '/mailings': 'Mailings',
  '/platform': 'Platform',
  '/panel/user': 'User Panel',
  '/recommended/channels': 'Recommended Channels',
  '/recommended/scores': 'Recommended Scores',
  '/twitch/games': 'Twitch Games',
  '/videos': 'Videos',
  '/tasks': 'Tasks',
  '/share_links': 'Share Links',
  '/import_influencers': 'Import Influencers',
  '/finances': 'Finances',
};

function resolveRouteTitle(pathname: string): string | null {
  // Exact match first
  if (ROUTE_TITLES[pathname]) return ROUTE_TITLES[pathname];

  // Try progressively shorter prefixes: /a/b/c -> /a/b -> /a
  const segments = pathname.split('/').filter(Boolean);
  for (let i = segments.length - 1; i >= 1; i--) {
    const prefix = '/' + segments.slice(0, i).join('/');
    if (ROUTE_TITLES[prefix]) return ROUTE_TITLES[prefix];
  }

  return null;
}

interface PageTitleContextValue {
  setCampaignName: (name: string | null) => void;
}

const PageTitleContext = createContext<PageTitleContextValue>({
  setCampaignName: () => {},
});

export function useSetCampaignTitle() {
  return useContext(PageTitleContext).setCampaignName;
}

export function PageTitleProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [campaignName, setCampaignName] = useState<string | null>(null);
  const runtimeEnv = detectRuntimeEnvFromBrowser();
  const beTargetEnv = detectBeTargetEnvFromBrowser();
  // Env suffix appears ONLY on local dev; deployed titles stay clean (no suffix).
  // Show the BE target as "production" rather than the internal 'live' label.
  const envLabel = runtimeEnv === 'local'
    ? `local:${beTargetEnv === 'live' ? 'production' : 'staging'}`
    : null;

  // Reset campaign name on route change
  useEffect(() => {
    setCampaignName(null);
  }, [location.pathname]);

  // Update document.title
  useEffect(() => {
    const menuTitle = resolveRouteTitle(location.pathname);
    if (!menuTitle) {
      document.title = envLabel ? `[v2] Admin (${envLabel})` : '[v2] Admin';
      return;
    }

    const parts = [menuTitle];
    if (campaignName) parts.push(campaignName);
    const base = `[v2] ${parts.join('::')}`;
    document.title = envLabel ? `${base} (${envLabel})` : base;
  }, [location.pathname, campaignName, envLabel]);

  const setCampaignNameStable = useCallback((name: string | null) => {
    setCampaignName(name);
  }, []);

  return (
    <PageTitleContext.Provider value={{ setCampaignName: setCampaignNameStable }}>
      {children}
    </PageTitleContext.Provider>
  );
}
